import Feature from "ol/Feature";
import type { Map as OlMap } from "ol";
import type { Coordinate } from "ol/coordinate";
import { closestOnSegment } from "ol/coordinate";
import { primaryAction } from "ol/events/condition";
import PointerInteraction from "ol/interaction/Pointer";
import type MapBrowserEvent from "ol/MapBrowserEvent";
import Circle from "ol/geom/Circle";
import Point from "ol/geom/Point";
import Polygon from "ol/geom/Polygon";
import MultiPolygon from "ol/geom/MultiPolygon";
import type Geometry from "ol/geom/Geometry";
import { fromCircle } from "ol/geom/Polygon";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { createEditingStyle } from "ol/style/Style";

function pixelDistance(map: OlMap, a: Coordinate, b: Coordinate): number {
  const pa = map.getPixelFromCoordinate(a);
  const pb = map.getPixelFromCoordinate(b);
  return Math.hypot(pa[0] - pb[0], pa[1] - pb[1]);
}

function deepCloneCoords<R>(coords: R): R {
  return JSON.parse(JSON.stringify(coords)) as R;
}

/**
 * Anneau fermé OL : le dernier sommet du tableau duplique le premier.
 * Après translation du segment (i, i+1), il faut recoller les deux copies du même coin :
 * - segment qui touche l’index 0 : copier sommet 0 → dernier ;
 * - segment de fermeture (i+1 === n-1) : copier dernier → sommet 0 (sinon on écrase le déplacement du doublon).
 */
function syncClosedRingAfterEdgeMove(ring: Coordinate[], edgeIndex: number): void {
  const n = ring.length;
  if (n < 3) {
    return;
  }
  const i = edgeIndex;
  const first = ring[0];
  const last = ring[n - 1];
  const copyCoord = (from: Coordinate, to: Coordinate): void => {
    for (let d = 0; d < Math.min(from.length, to.length); d++) {
      to[d] = from[d];
    }
  };
  if (i + 1 === n - 1) {
    copyCoord(last, first);
  } else {
    copyCoord(first, last);
  }
}

type EdgePick =
  | {
      kind: "Polygon";
      ringIndex: number;
      edgeIndex: number;
      score: number;
    }
  | {
      kind: "MultiPolygon";
      polyIndex: number;
      ringIndex: number;
      edgeIndex: number;
      score: number;
    };

type ActiveEdge =
  | { kind: "Polygon"; ringIndex: number; edgeIndex: number }
  | {
      kind: "MultiPolygon";
      polyIndex: number;
      ringIndex: number;
      edgeIndex: number;
    };

/**
 * Choisit le segment le plus « sous le pointeur » en coordonnées écran,
 * en excluant une zone autour de chaque sommet pour éviter la sélection d’un seul vertex.
 */
function pickEdge(
  map: OlMap,
  coordinate: Coordinate,
  geometry: Geometry,
  pixelTolerance: number,
  vertexExclusionPx: number,
): EdgePick | null {
  let best: EdgePick | null = null;

  if (geometry.getType() === "Polygon") {
    const poly = geometry as Polygon;
    const rings = poly.getCoordinates();
    for (let r = 0; r < rings.length; r++) {
      const ring = rings[r];
      for (let i = 0; i < ring.length - 1; i++) {
        const seg: [Coordinate, Coordinate] = [ring[i], ring[i + 1]];
        const closest = closestOnSegment(coordinate, seg);
        const dVert = Math.min(
          pixelDistance(map, coordinate, seg[0]),
          pixelDistance(map, coordinate, seg[1]),
        );
        if (dVert < vertexExclusionPx) {
          continue;
        }
        const dEdge = pixelDistance(map, coordinate, closest);
        if (dEdge > pixelTolerance) {
          continue;
        }
        if (!best || dEdge < best.score) {
          best = { kind: "Polygon", ringIndex: r, edgeIndex: i, score: dEdge };
        }
      }
    }
  } else if (geometry.getType() === "MultiPolygon") {
    const mp = geometry as MultiPolygon;
    const polys = mp.getCoordinates();
    for (let p = 0; p < polys.length; p++) {
      const rings = polys[p];
      for (let r = 0; r < rings.length; r++) {
        const ring = rings[r];
        for (let i = 0; i < ring.length - 1; i++) {
          const seg: [Coordinate, Coordinate] = [ring[i], ring[i + 1]];
          const closest = closestOnSegment(coordinate, seg);
          const dVert = Math.min(
            pixelDistance(map, coordinate, seg[0]),
            pixelDistance(map, coordinate, seg[1]),
          );
          if (dVert < vertexExclusionPx) {
            continue;
          }
          const dEdge = pixelDistance(map, coordinate, closest);
          if (dEdge > pixelTolerance) {
            continue;
          }
          if (!best || dEdge < best.score) {
            best = {
              kind: "MultiPolygon",
              polyIndex: p,
              ringIndex: r,
              edgeIndex: i,
              score: dEdge,
            };
          }
        }
      }
    }
  }

  return best;
}

function getSegmentCoordinates(
  geometry: Geometry,
  hit: EdgePick,
): [Coordinate, Coordinate] | null {
  if (hit.kind === "Polygon") {
    const poly = geometry as Polygon;
    const ring = poly.getCoordinates()[hit.ringIndex];
    const i = hit.edgeIndex;
    return [ring[i], ring[i + 1]];
  }
  const mp = geometry as MultiPolygon;
  const ring = mp.getCoordinates()[hit.polyIndex][hit.ringIndex];
  const i = hit.edgeIndex;
  return [ring[i], ring[i + 1]];
}

/** Cohérent avec l’octogone / conversion Circle legacy dans l’éditeur d’override. */
const CIRCLE_AS_POLYGON_SIDES = 8;

export type EdgeMoveInteractionOptions = {
  source: VectorSource;
  layer: VectorLayer<VectorSource>;
  /** Tolérance pixel pour considérer le pointeur « sur » un côté (aligné sur Snap, ~10). */
  pixelTolerance?: number;
  /** Zone autour de chaque sommet où on refuse le mode « côté » (pour ne pas confondre avec un vertex). */
  vertexExclusionPx?: number;
};

/**
 * Déplace un segment polygonal comme un bloc : les deux extrémités suivent le même déplacement que la souris.
 * À utiliser avec {@link module:ol/interaction/Snap} ajouté après cet outil dans la collection pour que le snap
 * s’applique avant (voir insertAt(0, ...) sur la carte).
 */
export default class EdgeMoveInteraction extends PointerInteraction {
  private readonly source_: VectorSource;

  private readonly layer_: VectorLayer<VectorSource>;

  private readonly pixelTolerance_: number;

  private readonly vertexExclusionPx_: number;

  private anchor_: Coordinate | null = null;

  /** Copie profonde des coordonnées au pointerdown. */
  private snapshotPolygon_: Coordinate[][] | null = null;

  private snapshotMulti_: Coordinate[][][] | null = null;

  private active_: ActiveEdge | null = null;

  private activeFeature_: Feature | null = null;

  /** Point sur le côté sous le curseur (même style que Modify / sommets). */
  private indicatorSource_: VectorSource | null = null;

  private indicatorLayer_: VectorLayer<VectorSource> | null = null;

  private indicatorFeature_: Feature | null = null;

  constructor(options: EdgeMoveInteractionOptions) {
    super({
      stopDown: (handled) => handled,
    });

    this.source_ = options.source;
    this.layer_ = options.layer;
    this.pixelTolerance_ = options.pixelTolerance ?? 12;
    this.vertexExclusionPx_ = options.vertexExclusionPx ?? 10;
  }

  private prepareGeometry_(feature: Feature): Geometry | null {
    let geom = feature.getGeometry();
    if (!geom) {
      return null;
    }
    if (geom.getType() === "Circle") {
      const poly = fromCircle(geom as Circle, CIRCLE_AS_POLYGON_SIDES);
      feature.setGeometry(poly);
      geom = poly;
    }
    if (geom.getType() !== "Polygon" && geom.getType() !== "MultiPolygon") {
      return null;
    }
    return geom;
  }

  /**
   * Géométrie pour détection au survol sans modifier la feature (cercle → polygone temporaire).
   */
  private geometryForHoverPick_(feature: Feature): Geometry | null {
    const geom = feature.getGeometry();
    if (!geom) {
      return null;
    }
    if (geom.getType() === "Circle") {
      return fromCircle(geom as Circle, CIRCLE_AS_POLYGON_SIDES);
    }
    if (geom.getType() === "Polygon" || geom.getType() === "MultiPolygon") {
      return geom;
    }
    return null;
  }

  private clearIndicator_(): void {
    if (this.indicatorFeature_ && this.indicatorSource_) {
      this.indicatorSource_.removeFeature(this.indicatorFeature_);
      this.indicatorFeature_ = null;
    }
  }

  private ensureIndicatorLayer_(map: OlMap): void {
    if (this.indicatorLayer_ && this.indicatorSource_) {
      return;
    }
    this.indicatorSource_ = new VectorSource();
    const editingStyles = createEditingStyle();
    this.indicatorLayer_ = new VectorLayer({
      source: this.indicatorSource_,
      style: editingStyles.Point,
      zIndex: 25,
    });
  }

  setMap(map: OlMap | null): void {
    const prev = this.getMap();
    if (prev && this.indicatorLayer_) {
      prev.removeLayer(this.indicatorLayer_);
    }
    super.setMap(map);
    if (map) {
      this.ensureIndicatorLayer_(map);
      map.addLayer(this.indicatorLayer_!);
    }
  }

  handleMoveEvent(evt: MapBrowserEvent): void {
    if (this.handlingDownUpSequence) {
      return;
    }
    const map = evt.map;
    const feature = map.forEachFeatureAtPixel(evt.pixel, (f) => f, {
      layerFilter: (l) => l === this.layer_,
      hitTolerance: 8,
    }) as Feature | undefined;
    if (!feature || !this.indicatorSource_) {
      this.clearIndicator_();
      return;
    }
    const geometry = this.geometryForHoverPick_(feature);
    if (!geometry) {
      this.clearIndicator_();
      return;
    }
    const hit = pickEdge(
      map,
      evt.coordinate,
      geometry,
      this.pixelTolerance_,
      this.vertexExclusionPx_,
    );
    if (!hit) {
      this.clearIndicator_();
      return;
    }
    const seg = getSegmentCoordinates(geometry, hit);
    if (!seg) {
      this.clearIndicator_();
      return;
    }
    const closest = closestOnSegment(evt.coordinate, seg);
    if (!this.indicatorFeature_) {
      this.indicatorFeature_ = new Feature(new Point(closest));
      this.indicatorSource_.addFeature(this.indicatorFeature_);
    } else {
      const g = this.indicatorFeature_.getGeometry();
      if (g instanceof Point) {
        g.setCoordinates(closest);
      }
    }
  }

  handleDownEvent(evt: MapBrowserEvent): boolean {
    if (!primaryAction(evt)) {
      return false;
    }
    const map = evt.map;
    const feature = map.forEachFeatureAtPixel(evt.pixel, (f) => f, {
      layerFilter: (l) => l === this.layer_,
      hitTolerance: 8,
    }) as Feature | undefined;
    if (!feature) {
      return false;
    }

    const geometry = this.prepareGeometry_(feature);
    if (!geometry) {
      return false;
    }

    const hit = pickEdge(
      map,
      evt.coordinate,
      geometry,
      this.pixelTolerance_,
      this.vertexExclusionPx_,
    );
    if (!hit) {
      return false;
    }

    this.clearIndicator_();

    if (geometry.getType() === "Polygon") {
      this.snapshotPolygon_ = deepCloneCoords((geometry as Polygon).getCoordinates());
      this.snapshotMulti_ = null;
    } else {
      this.snapshotMulti_ = deepCloneCoords((geometry as MultiPolygon).getCoordinates());
      this.snapshotPolygon_ = null;
    }

    this.anchor_ = evt.coordinate.slice() as Coordinate;
    if (hit.kind === "Polygon") {
      this.active_ = {
        kind: "Polygon",
        ringIndex: hit.ringIndex,
        edgeIndex: hit.edgeIndex,
      };
    } else {
      this.active_ = {
        kind: "MultiPolygon",
        polyIndex: hit.polyIndex,
        ringIndex: hit.ringIndex,
        edgeIndex: hit.edgeIndex,
      };
    }
    this.activeFeature_ = feature;
    return true;
  }

  handleDragEvent(evt: MapBrowserEvent): void {
    if (
      !this.anchor_ ||
      !this.active_ ||
      !this.activeFeature_ ||
      (!this.snapshotPolygon_ && !this.snapshotMulti_)
    ) {
      return;
    }

    const geom = this.activeFeature_.getGeometry();
    if (!geom) {
      return;
    }

    const delta = evt.coordinate.map((v, d) => v - this.anchor_![d]);

    const translateCoord = (c: Coordinate, snap: Coordinate): void => {
      for (let d = 0; d < Math.min(c.length, delta.length, snap.length); d++) {
        c[d] = snap[d] + delta[d];
      }
    };

    if (this.active_.kind === "Polygon" && this.snapshotPolygon_) {
      const coords = deepCloneCoords(this.snapshotPolygon_);
      const { ringIndex, edgeIndex } = this.active_;
      const ring = coords[ringIndex];
      const snapRing = this.snapshotPolygon_[ringIndex];
      const i = edgeIndex;
      translateCoord(ring[i], snapRing[i]);
      translateCoord(ring[i + 1], snapRing[i + 1]);
      syncClosedRingAfterEdgeMove(ring, edgeIndex);
      (geom as Polygon).setCoordinates(coords);
    } else if (this.active_.kind === "MultiPolygon" && this.snapshotMulti_) {
      const coords = deepCloneCoords(this.snapshotMulti_);
      const { polyIndex, ringIndex, edgeIndex } = this.active_;
      const ring = coords[polyIndex][ringIndex];
      const snapRing = this.snapshotMulti_[polyIndex][ringIndex];
      const i = edgeIndex;
      translateCoord(ring[i], snapRing[i]);
      translateCoord(ring[i + 1], snapRing[i + 1]);
      syncClosedRingAfterEdgeMove(ring, edgeIndex);
      (geom as MultiPolygon).setCoordinates(coords);
    }

    geom.changed();
    this.activeFeature_.changed();
  }

  handleUpEvent(): boolean {
    this.anchor_ = null;
    this.snapshotPolygon_ = null;
    this.snapshotMulti_ = null;
    this.active_ = null;
    this.activeFeature_ = null;
    return false;
  }
}
